// Developer-only health vault verification tool.
// Accessible only at /dev/health-vault and only to OWNER_ID.
// Exercises the full encryption stack end-to-end.
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { deriveKey, encrypt, decrypt, getCachedKey, unlockHealthVault, lockHealthVault } from '../../utils/healthCrypto'
import { logAccess } from '../../utils/accessLog'

const CANARY = 'casavio_canary_v1'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14', muted: '#9A7B68',
  success: '#2E6B4A', successBg: '#EBF5EF',
  danger: '#B03020', dangerBg: '#FDECEA',
  info: '#1A4A7A', infoBg: '#EBF2FA',
  code: '#F5F0E8', codeBorder: '#C8B89A',
}
const sans = 'Calibri, sans-serif'
const mono = '"Courier New", monospace'

function Section({ title, children, accent }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${accent || C.border}`,
      borderRadius: 10, padding: 20, marginBottom: 16,
    }}>
      <h3 style={{ margin: '0 0 14px', color: C.brown, fontFamily: 'Georgia, serif', fontSize: 16 }}>
        {title}
      </h3>
      {children}
    </div>
  )
}

function Tag({ color, bg, children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 12, fontFamily: sans, fontWeight: 600,
      color: color || C.success, background: bg || C.successBg,
    }}>
      {children}
    </span>
  )
}

function Btn({ onClick, disabled, danger, children, small }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '6px 12px' : '9px 16px',
        borderRadius: 7, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: danger ? C.danger : C.amber, color: '#fff',
        fontSize: small ? 13 : 14, fontFamily: sans,
        opacity: disabled ? 0.6 : 1, marginRight: 8,
      }}
    >
      {children}
    </button>
  )
}

function StatusLine({ label, value, ok }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 14 }}>
      <span style={{ color: C.muted, minWidth: 180 }}>{label}</span>
      <Tag color={ok ? C.success : C.danger} bg={ok ? C.successBg : C.dangerBg}>
        {value}
      </Tag>
    </div>
  )
}

export default function HealthVaultDemo({ session }) {
  const userId = session.user.id

  const [profile, setProfile] = useState(null)
  const [homeId, setHomeId] = useState(null)
  const [password, setPassword] = useState('')
  const [vaultUnlocked, setVaultUnlocked] = useState(false)
  const [medications, setMedications] = useState(null)
  const [rawRows, setRawRows] = useState(null)
  const [accessLogs, setAccessLogs] = useState(null)
  const [newMed, setNewMed] = useState('')
  const [status, setStatus] = useState([])
  const [busy, setBusy] = useState(false)

  const log = useCallback((msg, type = 'info') => {
    setStatus(prev => [{
      msg,
      type,
      ts: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 20))
  }, [])

  // Load profile and home on mount
  useEffect(() => {
    async function load() {
      const { data: prof } = await supabase
        .from('profiles')
        .select('encryption_salt, encryption_initialized, encryption_canary')
        .eq('id', userId)
        .single()
      setProfile(prof)

      const { data: memberships } = await supabase
        .from('home_members')
        .select('home_id')
        .eq('user_id', userId)
        .limit(1)
      if (memberships?.length) setHomeId(memberships[0].home_id)

      setVaultUnlocked(getCachedKey() !== null)
    }
    load()
  }, [userId])

  // ── 1. SETUP ENCRYPTION (first-time) ──────────────────────────────────────
  async function handleSetup() {
    setBusy(true)
    try {
      const saltBytes = crypto.getRandomValues(new Uint8Array(32))
      const saltBase64 = btoa(String.fromCharCode(...saltBytes))
      const key = await deriveKey(password, saltBase64)
      const canaryCiphertext = await encrypt(CANARY, key)

      const { error } = await supabase.from('profiles').update({
        encryption_salt: saltBase64,
        encryption_canary: canaryCiphertext,
        encryption_initialized: true,
      }).eq('id', userId)

      if (error) throw error

      unlockHealthVault(key)
      setVaultUnlocked(true)
      setProfile(p => ({ ...p, encryption_salt: saltBase64, encryption_canary: canaryCiphertext, encryption_initialized: true }))
      log('Encryption initialized. Salt and canary saved to profiles. Vault is now unlocked.', 'success')
    } catch (e) {
      log(`Setup failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
      setPassword('')
    }
  }

  // ── 2. UNLOCK VAULT ───────────────────────────────────────────────────────
  async function handleUnlock() {
    setBusy(true)
    try {
      const key = await deriveKey(password, profile.encryption_salt)
      const canary = await decrypt(profile.encryption_canary, key)
      if (canary !== CANARY) throw new Error('Canary mismatch — unexpected value after decryption')
      unlockHealthVault(key)
      setVaultUnlocked(true)
      log('Vault unlocked. Key cached in memory (not in localStorage or sessionStorage).', 'success')
    } catch (e) {
      const msg = e.name === 'OperationError' ? 'Wrong password — AES-GCM decryption failed' : e.message
      log(`Unlock failed: ${msg}`, 'error')
    } finally {
      setBusy(false)
      setPassword('')
    }
  }

  // ── 3. LOCK VAULT ─────────────────────────────────────────────────────────
  function handleLock() {
    lockHealthVault()
    setVaultUnlocked(false)
    setMedications(null)
    log('Vault locked. Cached key wiped from memory.', 'info')
  }

  // ── 4. ADD MEDICATION ─────────────────────────────────────────────────────
  async function handleAddMedication() {
    if (!homeId) { log('No home_id found — join a home first.', 'error'); return }
    setBusy(true)
    try {
      const key = getCachedKey()
      if (!key) throw new Error('Vault is locked')
      const encryptedValue = await encrypt(newMed, key)
      const encryptedNotes = await encrypt('Added from dev health vault demo', key)

      const { data, error } = await supabase.from('health_information').insert({
        home_id: homeId,
        category: 'medication',
        encrypted_value: encryptedValue,
        encrypted_notes: encryptedNotes,
        created_by: userId,
      }).select().single()

      if (error) throw error

      await logAccess({ resourceType: 'health_information', resourceId: data.id, homeId, action: 'create' })
      log(`Medication inserted. Row ID: ${data.id}. Ciphertext stored — no plaintext in DB.`, 'success')
      setNewMed('')
    } catch (e) {
      log(`Add medication failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  // ── 5. LIST MEDICATIONS (decrypted) ──────────────────────────────────────
  async function handleListMedications() {
    if (!homeId) { log('No home_id found.', 'error'); return }
    setBusy(true)
    try {
      const key = getCachedKey()
      if (!key) {
        log('Vault is locked — cannot decrypt. Lock test confirmed.', 'error')
        setMedications([])
        setBusy(false)
        return
      }

      const { data, error } = await supabase
        .from('health_information')
        .select('*')
        .eq('home_id', homeId)
        .eq('category', 'medication')
        .order('created_at', { ascending: false })

      if (error) throw error

      await logAccess({ resourceType: 'health_information', homeId, action: 'view' })

      const decrypted = await Promise.all(data.map(async row => {
        try {
          const value = await decrypt(row.encrypted_value, key)
          const notes = row.encrypted_notes ? await decrypt(row.encrypted_notes, key) : null
          return { ...row, decryptedValue: value, decryptedNotes: notes }
        } catch {
          return { ...row, decryptedValue: '[decryption failed]', decryptedNotes: null }
        }
      }))

      setMedications(decrypted)
      log(`Fetched and decrypted ${decrypted.length} medication row(s).`, 'success')
    } catch (e) {
      log(`List medications failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  // ── 6. SHOW RAW DB ROWS ───────────────────────────────────────────────────
  async function handleShowRaw() {
    if (!homeId) { log('No home_id found.', 'error'); return }
    setBusy(true)
    try {
      const { data, error } = await supabase
        .from('health_information')
        .select('id, category, encrypted_value, encrypted_notes, created_at')
        .eq('home_id', homeId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setRawRows(data)
      log(`Fetched ${data.length} raw row(s) from health_information. Confirm no plaintext visible below.`, 'info')
    } catch (e) {
      log(`Show raw failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  // ── 7. SHOW ACCESS LOG ────────────────────────────────────────────────────
  async function handleShowAccessLog() {
    setBusy(true)
    try {
      const { data, error } = await supabase
        .from('access_log')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      await logAccess({ resourceType: 'access_log', homeId: homeId || undefined, action: 'view' })
      setAccessLogs(data)
      log(`Fetched ${data.length} access log entry(s).`, 'info')
    } catch (e) {
      log(`Show access log failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  // ── 8. RESET ──────────────────────────────────────────────────────────────
  async function handleReset() {
    if (!window.confirm('Delete all health_information rows for your user and clear encryption setup?')) return
    setBusy(true)
    try {
      if (homeId) {
        await logAccess({ resourceType: 'health_information', homeId, action: 'delete' })
        await supabase.from('health_information').delete().eq('home_id', homeId).eq('created_by', userId)
      }

      await supabase.from('profiles').update({
        encryption_salt: null,
        encryption_canary: null,
        encryption_initialized: false,
      }).eq('id', userId)

      lockHealthVault()
      setVaultUnlocked(false)
      setProfile(p => ({ ...p, encryption_salt: null, encryption_canary: null, encryption_initialized: false }))
      setMedications(null)
      setRawRows(null)
      log('Reset complete. All health_information rows deleted. Encryption cleared from profile.', 'info')
    } catch (e) {
      log(`Reset failed: ${e.message}`, 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!profile) return (
    <div style={{ padding: 40, fontFamily: sans, color: C.muted }}>Loading profile…</div>
  )

  const initialized = profile.encryption_initialized

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: 24, fontFamily: sans }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0, color: C.brown, fontFamily: 'Georgia, serif', fontSize: 24 }}>
              Health Vault — Dev Verification
            </h1>
            <Tag color={C.danger} bg={C.dangerBg}>INTERNAL ONLY</Tag>
          </div>
          <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>
            Route: /dev/health-vault · Accessible to kenneth@casavio only · Not visible to any other user
          </p>
        </div>

        {/* 1. Status */}
        <Section title="1. Current State">
          <StatusLine label="Encryption initialized" value={initialized ? 'yes' : 'no'} ok={initialized} />
          <StatusLine label="Vault" value={vaultUnlocked ? 'UNLOCKED' : 'LOCKED'} ok={vaultUnlocked} />
          <StatusLine label="home_id found" value={homeId ? homeId.slice(0, 8) + '…' : 'none'} ok={!!homeId} />
          <StatusLine label="Key in memory" value={getCachedKey() ? 'yes (CryptoKey object)' : 'no'} ok={vaultUnlocked} />
        </Section>

        {/* 2. Setup / Unlock / Lock */}
        <Section title={initialized ? (vaultUnlocked ? '2. Vault Unlocked' : '2. Unlock Vault') : '2. Setup Encryption (First Time)'}>
          {vaultUnlocked ? (
            <div>
              <p style={{ margin: '0 0 12px', color: C.muted, fontSize: 14 }}>
                Key is cached in memory. It will be wiped when you lock or reload.
              </p>
              <Btn onClick={handleLock}>Lock Vault</Btn>
            </div>
          ) : (
            <div>
              <p style={{ margin: '0 0 12px', color: C.muted, fontSize: 14 }}>
                {initialized
                  ? 'Enter your Casavio password to derive the encryption key and verify the canary.'
                  : 'First-time setup: enter a password to generate a salt, derive a key, and store the encrypted canary.'}
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (initialized ? handleUnlock() : handleSetup())}
                  style={{
                    padding: '8px 12px', borderRadius: 7, border: `1px solid ${C.border}`,
                    fontSize: 14, fontFamily: sans, width: 260,
                  }}
                />
                <Btn onClick={initialized ? handleUnlock : handleSetup} disabled={busy || !password}>
                  {initialized ? 'Unlock' : 'Setup Encryption'}
                </Btn>
              </div>
            </div>
          )}
        </Section>

        {/* 3. Add Medication */}
        <Section title="3. Add Test Medication" accent={vaultUnlocked ? C.border : C.codeBorder}>
          {!vaultUnlocked && (
            <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Unlock the vault first.</p>
          )}
          {vaultUnlocked && (
            <div>
              <p style={{ margin: '0 0 10px', color: C.muted, fontSize: 14 }}>
                Value will be encrypted client-side before insertion. The DB row will contain only ciphertext.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder='e.g. "lisinopril 10mg, once daily with water"'
                  value={newMed}
                  onChange={e => setNewMed(e.target.value)}
                  style={{
                    padding: '8px 12px', borderRadius: 7, border: `1px solid ${C.border}`,
                    fontSize: 14, fontFamily: sans, flex: 1,
                  }}
                />
                <Btn onClick={handleAddMedication} disabled={busy || !newMed.trim()}>
                  Encrypt & Insert
                </Btn>
              </div>
            </div>
          )}
        </Section>

        {/* 4. List Medications (decrypted) */}
        <Section title="4. List Medications (Decrypted View)">
          <Btn onClick={handleListMedications} disabled={busy} small>
            {vaultUnlocked ? 'Fetch & Decrypt' : 'Fetch (locked — expect failure)'}
          </Btn>
          {medications !== null && (
            <div style={{ marginTop: 12 }}>
              {medications.length === 0 && (
                <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>No rows returned.</p>
              )}
              {medications.map(m => (
                <div key={m.id} style={{
                  background: m.decryptedValue === '[decryption failed]' ? C.dangerBg : C.successBg,
                  border: `1px solid ${m.decryptedValue === '[decryption failed]' ? C.danger : C.success}`,
                  borderRadius: 7, padding: '10px 14px', marginBottom: 8,
                }}>
                  <div style={{ fontSize: 14, color: C.brown, marginBottom: 4 }}>
                    <strong>Decrypted value:</strong> {m.decryptedValue}
                  </div>
                  {m.decryptedNotes && (
                    <div style={{ fontSize: 13, color: C.muted }}>
                      <strong>Notes:</strong> {m.decryptedNotes}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                    ID: {m.id} · {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 5. Raw DB Rows */}
        <Section title="5. Raw Database View (Confirm No Plaintext)">
          <p style={{ margin: '0 0 10px', color: C.muted, fontSize: 14 }}>
            These are the actual values stored in Supabase. Verify that <code>encrypted_value</code> is opaque base64 ciphertext with no readable content.
          </p>
          <Btn onClick={handleShowRaw} disabled={busy} small>Show Raw Rows</Btn>
          {rawRows !== null && (
            <div style={{ marginTop: 12 }}>
              {rawRows.length === 0 && (
                <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>No rows found.</p>
              )}
              {rawRows.map(row => (
                <div key={row.id} style={{
                  background: C.code, border: `1px solid ${C.codeBorder}`,
                  borderRadius: 7, padding: '10px 14px', marginBottom: 8, fontFamily: mono, fontSize: 12,
                }}>
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: C.muted }}>id: </span>{row.id}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: C.muted }}>category: </span>
                    <Tag>{row.category}</Tag>
                  </div>
                  <div style={{ marginBottom: 4, wordBreak: 'break-all' }}>
                    <span style={{ color: C.muted }}>encrypted_value: </span>
                    <span style={{ color: C.danger }}>{row.encrypted_value}</span>
                  </div>
                  {row.encrypted_notes && (
                    <div style={{ wordBreak: 'break-all' }}>
                      <span style={{ color: C.muted }}>encrypted_notes: </span>
                      <span style={{ color: C.danger }}>{row.encrypted_notes}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 6. Access Log */}
        <Section title="6. Access Log (Audit Trail)">
          <Btn onClick={handleShowAccessLog} disabled={busy} small>Show Access Log</Btn>
          {accessLogs !== null && (
            <div style={{ marginTop: 12 }}>
              {accessLogs.length === 0 && (
                <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>No log entries yet.</p>
              )}
              {accessLogs.map(entry => (
                <div key={entry.id} style={{
                  borderBottom: `1px solid ${C.border}`, padding: '8px 0',
                  fontSize: 13, display: 'flex', gap: 12, alignItems: 'center',
                }}>
                  <Tag
                    color={entry.action === 'create' ? C.success : entry.action === 'delete' ? C.danger : C.info}
                    bg={entry.action === 'create' ? C.successBg : entry.action === 'delete' ? C.dangerBg : C.infoBg}
                  >
                    {entry.action}
                  </Tag>
                  <span style={{ color: C.muted }}>{entry.resource_type}</span>
                  {entry.resource_id && (
                    <span style={{ color: C.muted, fontFamily: mono, fontSize: 11 }}>
                      {entry.resource_id.slice(0, 8)}…
                    </span>
                  )}
                  <span style={{ color: C.muted, marginLeft: 'auto' }}>
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 7. Reset */}
        <Section title="7. Reset (for Repeated Testing)" accent={C.dangerBg}>
          <p style={{ margin: '0 0 10px', color: C.muted, fontSize: 14 }}>
            Deletes all health_information rows for your home, clears salt/canary on your profile. Locks the vault.
          </p>
          <Btn onClick={handleReset} disabled={busy} danger>Reset Everything</Btn>
        </Section>

        {/* Status Console */}
        <Section title="Console">
          {status.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>No actions taken yet.</p>
          )}
          {status.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '6px 0', borderBottom: i < status.length - 1 ? `1px solid ${C.border}` : 'none',
              fontSize: 13,
            }}>
              <span style={{ color: C.muted, minWidth: 72, flexShrink: 0 }}>{s.ts}</span>
              <span style={{
                color: s.type === 'error' ? C.danger : s.type === 'success' ? C.success : C.brown,
              }}>
                {s.msg}
              </span>
            </div>
          ))}
        </Section>

      </div>
    </div>
  )
}
